// components/ui/AnimatedLogo.tsx
"use client";

import { useState, useRef, useEffect } from 'react';
import { useIsDesktop } from '../../hooks/useBreakpoint';
import Image from 'next/image';

interface AnimatedLogoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function AnimatedLogo({ className = '', size = 'md' }: AnimatedLogoProps) {
  const isDesktop = useIsDesktop();
  const logoRef = useRef<HTMLDivElement>(null);
  const [rotation, setRotation] = useState({ x: 0, y: 0 });

  const sizeClasses = {
    sm: { width: 64, height: 64, className: 'w-16 h-16' },
    md: { width: 96, height: 96, className: 'w-24 h-24' },
    lg: { width: 128, height: 128, className: 'w-32 h-32' }
  };

  useEffect(() => {
    if (!isDesktop || !logoRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = logoRef.current!.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const deltaX = (e.clientX - centerX) / rect.width;
      const deltaY = (e.clientY - centerY) / rect.height;
      
      // Omezíme rotaci na max ±15 stupňů
      const rotateX = Math.max(-15, Math.min(15, deltaY * -15));
      const rotateY = Math.max(-15, Math.min(15, deltaX * 15));
      
      setRotation({ x: rotateX, y: rotateY });
    };

    const handleMouseLeave = () => {
      setRotation({ x: 0, y: 0 });
    };

    const element = logoRef.current;
    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isDesktop]);

  const currentSize = sizeClasses[size];

  useEffect(() => {
    if (!isDesktop || !logoRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = logoRef.current!.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const deltaX = (e.clientX - centerX) / rect.width;
      const deltaY = (e.clientY - centerY) / rect.height;
      
      // Omezíme rotaci na max ±15 stupňů
      const rotateX = Math.max(-15, Math.min(15, deltaY * -15));
      const rotateY = Math.max(-15, Math.min(15, deltaX * 15));
      
      setRotation({ x: rotateX, y: rotateY });
    };

    const handleMouseLeave = () => {
      setRotation({ x: 0, y: 0 });
    };

    const element = logoRef.current;
    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isDesktop]);

  return (
    <div 
      ref={logoRef}
      className={`${currentSize.className} ${className} relative cursor-pointer`}
      style={{
        perspective: '1000px',
      }}
    >
      <div
        className="w-full h-full transition-transform duration-300 ease-out hover:scale-105"
        style={{
          transform: isDesktop 
            ? `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)` 
            : 'none',
          transformStyle: 'preserve-3d',
        }}
      >
        <Image
          src="/logo.png"
          alt="Logo"
          width={currentSize.width}
          height={currentSize.height}
          priority
          unoptimized
          className="w-full h-full object-contain drop-shadow-lg"
          style={{
            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15))',
          }}
        />
      </div>
    </div>
  );
}
